import { getAPIURL } from './config';
import type {
  Lesson,
  LessonContentType,
  Attachment,
  QuizMeta,
  QuizQuestion,
  QuizQuestionType,
  Course,
  BadgeStatus,
  TrainingStatus,
} from '../components/KnowledgeBase/Training/types';

// Для обратной совместимости
type OrganizationalCourse = Course;
type OrganizationalLesson = Lesson;
type OrganizationalStatus = TrainingStatus;
type OrganizationalAttachment = Attachment;
type ProfileLesson = Lesson;

// Типы для API ответов
export interface ApiImage {
  id: number;
  image: string;
  created_at: string;
}

export interface ApiBlock {
  id: number;
  type: string;
  text: string;
  video_url: string;
  file_url: string;
  media_url: string;
  order: number;
  created_at: string;
}

export interface ApiAnswerOption {
  id: number;
  text: string;
  is_correct: boolean;
  order: number;
  created_at: string;
}

export interface ApiTestQuestion {
  id: number;
  text: string;
  type: string;
  order: number;
  answer_options: ApiAnswerOption[];
  created_at: string;
}

export interface ApiTest {
  id: number;
  time_limit: string;
  questions: ApiTestQuestion[];
  created_at: string;
  updated_at: string;
}

export interface ApiLessonItem {
  id: number;
  text: string;
  image: string;
  material_file: string[];
  duration: string;
  difficulty: string;
  is_completed: boolean;
  blocks: ApiBlock[];
  test: ApiTest | null;
  created_at: string;
  updated_at: string;
}

export interface ApiLesson {
  id: number;
  user_id: string;
  type: string;
  name: string;
  description: string;
  goal: string;
  quiz: string[];
  tags: string[];
  access_level: number[];
  images: ApiImage[];
  lessons: ApiLessonItem[];
  completed_lessons_percentage: number;
  created_at: string;
  updated_at: string;
}

export interface ApiLessonsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiLesson[];
}

// Параметры запроса для получения уроков
export interface GetLessonsParams {
  user_id?: string;
  name__icontains?: string; // Фильтр по названию (contains, case-insensitive)
  description__icontains?: string; // Фильтр по описанию (contains, case-insensitive)
  type?: string; // Фильтр по типу (organizational or profile)
  tags?: string; // Фильтр по тегам (at least one tag must match)
  created_at__gte?: string;
  created_at__lte?: string;
  updated_at__gte?: string;
  updated_at__lte?: string;
  ordering?: string;
  page?: number; // minimum: 1
  page_size?: number; // minimum: 1, maximum: 100, default: 20
}

/**
 * Преобразует дату из формата ISO в формат "Обновлено DD.MM.YYYY"
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `Обновлено ${day}.${month}.${year}`;
  } catch {
    return 'Обновлено недавно';
  }
};

/**
 * Определяет тип контента урока на основе данных API
 */
const determineLessonType = (apiLesson: ApiLesson): LessonContentType => {
  if (apiLesson.quiz) {
    return 'quiz';
  }
  if (apiLesson.images && apiLesson.images.length > 0) {
    // Проверяем, есть ли видео (можно расширить логику)
    const hasVideo = apiLesson.images.some((img) =>
      img.image.toLowerCase().match(/\.(mp4|avi|mov|webm)$/i)
    );
    if (hasVideo) {
      return 'video';
    }
  }
  return 'document';
};

/**
 * Преобразует изображения из API в attachments
 */
const mapImagesToAttachments = (images: ApiImage[]): Attachment[] => {
  return images.map((img) => {
    const fileName = img.image.split('/').pop() || 'file';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    let fileType: 'pdf' | 'zip' | 'dwg' = 'pdf';
    if (extension === 'zip' || extension === 'rar') {
      fileType = 'zip';
    } else if (extension === 'dwg' || extension === 'dxf') {
      fileType = 'dwg';
    }

    // Примерный размер (можно получить из API позже)
    const size = '1.2 МБ';

    return {
      id: String(img.id),
      title: fileName,
      size,
      fileType,
    };
  });
};

/**
 * Преобразует quiz из API в QuizMeta
 * Примечание: quiz теперь массив строк согласно Swagger
 * Если нужна более сложная структура, возможно quiz содержит JSON строки
 */
const mapQuizToQuizMeta = (quiz: string[]): QuizMeta | undefined => {
  // Если quiz пустой или не определен, возвращаем undefined
  if (!quiz || !Array.isArray(quiz) || quiz.length === 0) {
    return undefined;
  }

  // Пытаемся распарсить каждую строку как JSON (если это JSON)
  // Или используем строки напрямую как вопросы
  const questions: QuizQuestion[] = [];
  
  quiz.forEach((quizItem: string, index: number) => {
    try {
      // Пытаемся распарсить как JSON
      const parsed = JSON.parse(quizItem);
      if (parsed && typeof parsed === 'object') {
        // Если это объект с вопросом
        questions.push({
          id: String(parsed.id || index),
          order: parsed.order || index + 1,
          text: parsed.text || parsed.question || quizItem,
          type: (parsed.type || 'single') as QuizQuestionType,
          options: parsed.options?.map((opt: any, optIndex: number) => ({
            id: String(opt.id || optIndex),
            label: opt.label || opt.text || opt,
          })),
          placeholder: parsed.placeholder,
        });
      } else {
        // Если это просто строка, создаем простой вопрос
        questions.push({
          id: String(index),
          order: index + 1,
          text: quizItem,
          type: 'single' as QuizQuestionType,
          options: [],
        });
      }
    } catch {
      // Если не JSON, используем как простой текст вопроса
      questions.push({
        id: String(index),
        order: index + 1,
        text: quizItem,
        type: 'single' as QuizQuestionType,
        options: [],
      });
    }
  });

  if (questions.length === 0) {
    return undefined;
  }

  return {
    duration: '10–15 минут',
    questionsCount: questions.length,
    passRequirement: `${Math.ceil(questions.length * 0.6)} из ${questions.length}`,
    questions,
  };
};

/**
 * Преобразует данные урока из API в формат Lesson
 */
export const mapApiLessonToLesson = (apiLesson: ApiLesson, index: number = 0): Lesson => {
  const lessonType = determineLessonType(apiLesson);
  const attachments = mapImagesToAttachments(apiLesson.images);
  
  // Используем name и description из новой структуры API
  const title = apiLesson.name || `Урок ${index + 1}`;
  const description = apiLesson.description || '';

  const lesson: Lesson = {
    id: String(apiLesson.id),
    order: index + 1,
    title,
    description,
    type: lessonType,
    duration: '10 мин', // Можно вычислить из данных или получить из API
    updatedAt: formatDate(apiLesson.updated_at),
    attachments,
  };

  // Добавляем quiz если есть
  if (apiLesson.quiz && apiLesson.quiz.length > 0) {
    const quizMeta = mapQuizToQuizMeta(apiLesson.quiz);
    if (quizMeta) {
      lesson.quiz = quizMeta;
    }
  }

  // Добавляем videoUrl если есть видео
  if (lessonType === 'video' && apiLesson.images.length > 0) {
    const videoImage = apiLesson.images.find((img) =>
      img.image.toLowerCase().match(/\.(mp4|avi|mov|webm)$/i)
    );
    if (videoImage) {
      lesson.videoUrl = videoImage.image;
      // Используем первое изображение как thumbnail
      if (apiLesson.images[0]) {
        lesson.videoThumbnail = apiLesson.images[0].image;
      }
    }
  } else if (apiLesson.images.length > 0) {
    // Для документов используем первое изображение как thumbnail
    lesson.videoThumbnail = apiLesson.images[0].image;
  }

  // Для документов можно добавить pages из description
  if (lessonType === 'document' && apiLesson.description) {
    const descriptionLines = apiLesson.description.split('\n').filter((line: string) => line.trim());
    if (descriptionLines.length > 1) {
      lesson.pages = descriptionLines;
    }
  }

  return lesson;
};

/**
 * Получить список уроков из API
 * @param params - Параметры запроса (фильтры, пагинация, сортировка)
 * @returns Promise с данными уроков
 */
export const getLessons = async (
  params: GetLessonsParams = {}
): Promise<ApiLessonsResponse> => {
  try {
    // Формируем query параметры
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    // Согласно Swagger, эндпоинт это просто /api/knowledge/, а не /api/knowledge/root_list/
    const baseUrl = getAPIURL();
    // Убираем trailing slash если есть, чтобы избежать двойных слешей
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${queryString ? `?${queryString}` : ''}`;

    console.log('Запрос к API:', url); // Для отладки
    console.log('Базовый URL:', baseUrl); // Для отладки

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении уроков:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(
        `Ошибка при получении уроков: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiLessonsResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе уроков:', error);
    throw error;
  }
};

/**
 * Получить конкретный урок по ID из API
 * @param id - ID урока
 * @returns Promise с данными урока
 */
export const getLessonById = async (id: string | number): Promise<ApiLesson> => {
  try {
    const baseUrl = getAPIURL();
    // Убираем trailing slash если есть, чтобы избежать двойных слешей
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    // Согласно Swagger, эндпоинт /knowledge/{id} без trailing slash
    const url = `${cleanBaseUrl}/knowledge/${id}`;

    console.log('Запрос урока по ID:', url); // Для отладки

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        id,
      });
      throw new Error(
        `Ошибка при получении урока: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiLesson = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе урока:', error);
    throw error;
  }
};

// Типы для недавно просмотренных уроков
export interface ApiRecentlyViewedLesson {
  lesson: ApiLessonItem & {
    id: number;
    blocks?: ApiBlock[];
    test?: ApiTest | null;
    material_file?: string[];
  };
  viewed_at: string;
}

export interface ApiRecentlyViewedResponse {
  count: number;
  results: ApiRecentlyViewedLesson[];
}

// Типы для прогресса курсов
export interface ApiCourseProgressItem {
  knowledge: ApiLesson;
  completion_percentage: number;
  completed_lessons: number;
  total_lessons: number;
  is_fully_completed: boolean;
  latest_completion_date: string | null;
}

export interface ApiCoursesProgressResponse {
  count: number;
  results: ApiCourseProgressItem[];
}

/**
 * Получить недавно просмотренные уроки
 * Бэкенд определяет пользователя по сессии (credentials: 'include')
 * @param limit - Максимальное количество уроков (1-100, по умолчанию 20)
 * @param cleanup - Очистить старые записи (старше 1 месяца)
 */
export const getRecentlyViewedLessons = async (
  limit: number = 20,
  cleanup: boolean = false
): Promise<ApiRecentlyViewedResponse> => {
  const baseUrl = getAPIURL();
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('cleanup', String(cleanup));
  const url = `${cleanBaseUrl}/knowledge/recently-viewed?${params}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Ошибка при получении недавно просмотренных уроков:', {
      status: response.status,
      errorText,
    });
    throw new Error(
      `Ошибка при получении недавно просмотренных уроков: ${response.status}`
    );
  }

  return response.json();
};

/**
 * Получить прогресс по всем курсам пользователя
 * Бэкенд определяет пользователя по сессии (credentials: 'include')
 */
export const getCoursesProgress = async (): Promise<ApiCoursesProgressResponse> => {
  const baseUrl = getAPIURL();
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const url = `${cleanBaseUrl}/knowledge/courses-progress`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Ошибка при получении прогресса курсов:', {
      status: response.status,
      errorText,
    });
    throw new Error(
      `Ошибка при получении прогресса курсов: ${response.status}`
    );
  }

  return response.json();
};

// Типы для создания и обновления
export interface CreateKnowledgeEntryRequest {
  user_id: string;
  type: string;
  name: string;
  description: string;
  goal: string;
  quiz?: string[];
  tags?: string[];
  access_level?: number[];
}

export interface UpdateKnowledgeEntryRequest {
  type?: string;
  name?: string;
  description?: string;
  goal?: string;
  quiz?: string[];
  tags?: string[];
  access_level?: number[];
}

export interface CreateBlockRequest {
  type: string;
  text?: string;
  video_url?: string;
  file_url?: string;
  media_url?: string;
  order?: number;
}

export interface CreateTestRequest {
  time_limit: string;
  questions?: CreateTestQuestionRequest[];
}

export interface CreateTestQuestionRequest {
  text: string;
  type: string;
  order: number;
  answer_options?: CreateAnswerOptionRequest[];
}

export interface CreateAnswerOptionRequest {
  text: string;
  is_correct: boolean;
  order: number;
}

export interface CreateLessonRequest {
  text: string;
  image?: string;
  material_file?: string | string[];
  duration?: string;
  difficulty?: string;
  is_completed?: boolean;
  blocks?: CreateBlockRequest[];
  test?: CreateTestRequest;
}

export interface UpdateLessonRequest {
  text?: string;
  image?: string;
  material_file?: string | string[];
  duration?: string;
  difficulty?: string;
  is_completed?: boolean;
}

/**
 * Создать новую запись в базе знаний
 * @param data - Данные для создания записи
 * @returns Promise с созданной записью
 */
export const createKnowledgeEntry = async (
  data: CreateKnowledgeEntryRequest
): Promise<ApiLesson> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        user_id: data.user_id,
        type: data.type,
        name: data.name,
        description: data.description,
        goal: data.goal,
        quiz: data.quiz || [],
        tags: data.tags || [],
        access_level: data.access_level || [],
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при создании записи:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(
        `Ошибка при создании записи: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiLesson = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при создании записи:', error);
    throw error;
  }
};

/**
 * Обновить запись в базе знаний
 * @param id - ID записи
 * @param data - Данные для обновления
 * @returns Promise с обновленной записью
 */
export const updateKnowledgeEntry = async (
  id: string | number,
  data: UpdateKnowledgeEntryRequest
): Promise<ApiLesson> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${id}`;

    const requestBody: any = {};
    if (data.type !== undefined) requestBody.type = data.type;
    if (data.name !== undefined) requestBody.name = data.name;
    if (data.description !== undefined) requestBody.description = data.description;
    if (data.goal !== undefined) requestBody.goal = data.goal;
    if (data.quiz !== undefined) requestBody.quiz = data.quiz;
    if (data.tags !== undefined) requestBody.tags = data.tags;
    if (data.access_level !== undefined) requestBody.access_level = data.access_level;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при обновлении записи:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        id,
      });
      throw new Error(
        `Ошибка при обновлении записи: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiLesson = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при обновлении записи:', error);
    throw error;
  }
};

/**
 * Загрузить изображение для записи в базе знаний
 * @param id - ID записи
 * @param imageFile - Файл изображения
 * @returns Promise с данными загруженного изображения
 */
export const uploadImage = async (
  id: string | number,
  imageFile: File
): Promise<ApiImage> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${id}/upload-image`;

    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при загрузке изображения:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        id,
      });
      throw new Error(
        `Ошибка при загрузке изображения: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiImage = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при загрузке изображения:', error);
    throw error;
  }
};

/**
 * Создать урок для записи в базе знаний
 * @param knowledgeId - ID записи в базе знаний
 * @param data - Данные для создания урока
 * @returns Promise с созданным уроком
 */
export const createLesson = async (
  knowledgeId: string | number,
  data: CreateLessonRequest
): Promise<ApiLessonItem> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons`;

    const requestBody = {
      text: data.text,
      image: data.image || '',
      material_file: data.material_file ? (Array.isArray(data.material_file) ? data.material_file : [data.material_file]) : [],
      duration: data.duration || '',
      difficulty: data.difficulty || 'легкая',
      is_completed: data.is_completed || false,
      blocks: data.blocks || [],
      test: data.test || null,
    };

    console.log('Создание урока:', {
      url,
      knowledgeId,
      requestBody: {
        ...requestBody,
        blocks: requestBody.blocks?.length || 0,
        text: requestBody.text?.substring(0, 100) + '...',
      },
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorText = '';
      let errorDetail = '';
      
      try {
        errorText = await response.text();
        // Попробуем распарсить как JSON
        try {
          const errorJson = JSON.parse(errorText);
          errorDetail = errorJson.detail || errorJson.message || errorText;
        } catch {
          errorDetail = errorText;
        }
      } catch (e) {
        errorText = 'Не удалось прочитать текст ошибки';
        errorDetail = errorText;
      }

      console.error('Ошибка при создании урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        errorDetail,
        knowledgeId,
        url,
        requestHeaders: {
          'Content-Type': 'application/json',
        },
      });

      // Более информативное сообщение об ошибке
      const errorMessage = errorDetail 
        ? `Ошибка при создании урока: ${response.status} ${response.statusText}. ${errorDetail}`
        : `Ошибка при создании урока: ${response.status} ${response.statusText}`;
      
      throw new Error(errorMessage);
    }

    const result: ApiLessonItem = await response.json();
    console.log('Урок успешно создан:', {
      lessonId: result.id,
      knowledgeId,
    });
    return result;
  } catch (error) {
    console.error('Ошибка при создании урока:', error);
    throw error;
  }
};

/**
 * Обновить урок для записи в базе знаний
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param data - Данные для обновления урока
 * @returns Promise с обновленным уроком
 */
export const updateLesson = async (
  knowledgeId: string | number,
  lessonId: string | number,
  data: UpdateLessonRequest
): Promise<ApiLessonItem> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}`;

    const requestBody: any = {};
    if (data.text !== undefined) requestBody.text = data.text;
    if (data.image !== undefined) requestBody.image = data.image;
    if (data.material_file !== undefined) {
      requestBody.material_file = Array.isArray(data.material_file) 
        ? data.material_file 
        : [data.material_file];
    }
    if (data.duration !== undefined) requestBody.duration = data.duration;
    if (data.difficulty !== undefined) requestBody.difficulty = data.difficulty;
    if (data.is_completed !== undefined) requestBody.is_completed = data.is_completed;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при обновлении урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при обновлении урока: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiLessonItem = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при обновлении урока:', error);
    throw error;
  }
};

/**
 * Получить список уроков для записи в базе знаний
 * @param knowledgeId - ID записи в базе знаний
 * @returns Promise с массивом уроков
 */
export const getLessonsByKnowledgeId = async (
  knowledgeId: string | number
): Promise<ApiLessonItem[]> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении уроков курса:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
      });
      throw new Error(
        `Ошибка при получении уроков курса: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiLessonItem[] = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе уроков курса:', error);
    throw error;
  }
};

/**
 * Получить конкретный урок по ID
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise с данными урока
 */
export const getLessonByKnowledgeAndLessonId = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<ApiLessonItem> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при получении урока: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiLessonItem = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе урока:', error);
    throw error;
  }
};

/**
 * Удалить запись в базе знаний
 * @param id - ID записи
 * @returns Promise<void>
 */
export const deleteKnowledgeEntry = async (
  id: string | number
): Promise<void> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${id}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      console.error('Ошибка при удалении записи:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        id,
      });
      throw new Error(
        `Ошибка при удалении записи: ${response.status} ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('Ошибка при удалении записи:', error);
    throw error;
  }
};

/**
 * Удалить урок для записи в базе знаний
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise<void>
 */
export const deleteLesson = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<void> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      console.error('Ошибка при удалении урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при удалении урока: ${response.status} ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('Ошибка при удалении урока:', error);
    throw error;
  }
};

/**
 * Удалить изображение для записи в базе знаний
 * @param knowledgeId - ID записи в базе знаний
 * @param imageId - ID изображения
 * @returns Promise<void>
 */
export const deleteImage = async (
  knowledgeId: string | number,
  imageId: string | number
): Promise<void> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/images/${imageId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      console.error('Ошибка при удалении изображения:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        imageId,
      });
      throw new Error(
        `Ошибка при удалении изображения: ${response.status} ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('Ошибка при удалении изображения:', error);
    throw error;
  }
};

/**
 * Загрузить материал для урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param file - Файл для загрузки
 * @returns Promise с URL загруженного файла
 */
export const uploadLessonMaterial = async (
  knowledgeId: string | number,
  lessonId: string | number,
  file: File
): Promise<string> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/upload-material`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при загрузке материала урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при загрузке материала урока: ${response.status} ${response.statusText}`
      );
    }

    const result: string = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при загрузке материала урока:', error);
    throw error;
  }
};

/**
 * Загрузить медиа для блока урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param blockId - ID блока
 * @param file - Файл для загрузки
 * @returns Promise с URL загруженного файла
 */
export const uploadBlockMedia = async (
  knowledgeId: string | number,
  lessonId: string | number,
  blockId: string | number,
  file: File
): Promise<string> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/blocks/${blockId}/upload-media`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при загрузке медиа блока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
        blockId,
      });
      throw new Error(
        `Ошибка при загрузке медиа блока: ${response.status} ${response.statusText}`
      );
    }

    const result: string = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при загрузке медиа блока:', error);
    throw error;
  }
};

/**
 * Отметить урок как пройденный
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise с обновленным уроком
 */
export const markLessonComplete = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<ApiLessonItem> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/complete`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при отметке урока как пройденного:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при отметке урока как пройденного: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiLessonItem = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при отметке урока как пройденного:', error);
    throw error;
  }
};

/**
 * Отметить урок как непройденный
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise с обновленным уроком
 */
export const markLessonIncomplete = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<ApiLessonItem> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/incomplete`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при отметке урока как непройденного:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при отметке урока как непройденного: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiLessonItem = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при отметке урока как непройденного:', error);
    throw error;
  }
};

/**
 * Создать тест для урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param data - Данные для создания теста
 * @returns Promise с созданным тестом
 */
export const createLessonTest = async (
  knowledgeId: string | number,
  lessonId: string | number,
  data: CreateTestRequest
): Promise<ApiTest> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        time_limit: data.time_limit,
        questions: data.questions || [],
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при создании теста:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при создании теста: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiTest = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при создании теста:', error);
    throw error;
  }
};

/**
 * Создать вопрос для теста урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param data - Данные для создания вопроса
 * @returns Promise с созданным вопросом
 */
export const createTestQuestion = async (
  knowledgeId: string | number,
  lessonId: string | number,
  data: CreateTestQuestionRequest
): Promise<ApiTestQuestion> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test/questions`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        text: data.text,
        type: data.type,
        order: data.order,
        answer_options: data.answer_options || [],
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при создании вопроса теста:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при создании вопроса теста: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiTestQuestion = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при создании вопроса теста:', error);
    throw error;
  }
};

/**
 * Создать вариант ответа для вопроса теста
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param questionId - ID вопроса
 * @param data - Данные для создания варианта ответа
 * @returns Promise с созданным вариантом ответа
 */
export const createAnswerOption = async (
  knowledgeId: string | number,
  lessonId: string | number,
  questionId: string | number,
  data: CreateAnswerOptionRequest
): Promise<ApiAnswerOption> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test/questions/${questionId}/options`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        text: data.text,
        is_correct: data.is_correct,
        order: data.order,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при создании варианта ответа:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
        questionId,
      });
      throw new Error(
        `Ошибка при создании варианта ответа: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiAnswerOption = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при создании варианта ответа:', error);
    throw error;
  }
};

export interface UpdateAnswerOptionRequest {
  text?: string;
  is_correct?: boolean;
  order?: number;
}

/**
 * Обновить вариант ответа на вопрос теста
 */
export const updateAnswerOption = async (
  knowledgeId: string | number,
  lessonId: string | number,
  questionId: string | number,
  optionId: string | number,
  data: UpdateAnswerOptionRequest
): Promise<ApiAnswerOption> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test/questions/${questionId}/options/${optionId}`;

    const body: Record<string, unknown> = {};
    if (data.text !== undefined) body.text = data.text;
    if (data.is_correct !== undefined) body.is_correct = data.is_correct;
    if (data.order !== undefined) body.order = data.order;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при обновлении варианта ответа:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
        questionId,
        optionId,
      });
      throw new Error(
        `Ошибка при обновлении варианта ответа: ${response.status} ${response.statusText}`
      );
    }

    const result: ApiAnswerOption = await response.json();
    return result;
  } catch (error) {
    console.error('Ошибка при обновлении варианта ответа:', error);
    throw error;
  }
};

/**
 * Получить тест урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise с данными теста
 */
export const getLessonTest = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<ApiTest> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении теста урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при получении теста урока: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiTest = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе теста урока:', error);
    throw error;
  }
};

/**
 * Удалить тест урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise<void>
 */
export const deleteLessonTest = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<void> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      console.error('Ошибка при удалении теста урока:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при удалении теста урока: ${response.status} ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('Ошибка при удалении теста урока:', error);
    throw error;
  }
};

/**
 * Получить список вопросов теста урока
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @returns Promise с массивом вопросов
 */
export const getTestQuestions = async (
  knowledgeId: string | number,
  lessonId: string | number
): Promise<ApiTestQuestion[]> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test/questions`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении вопросов теста:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
      });
      throw new Error(
        `Ошибка при получении вопросов теста: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiTestQuestion[] = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе вопросов теста:', error);
    throw error;
  }
};

/**
 * Получить конкретный вопрос теста
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param questionId - ID вопроса
 * @returns Promise с данными вопроса
 */
export const getTestQuestion = async (
  knowledgeId: string | number,
  lessonId: string | number,
  questionId: string | number
): Promise<ApiTestQuestion> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test/questions/${questionId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении вопроса теста:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
        questionId,
      });
      throw new Error(
        `Ошибка при получении вопроса теста: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiTestQuestion = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе вопроса теста:', error);
    throw error;
  }
};

/**
 * Получить список вариантов ответа для вопроса теста
 * @param knowledgeId - ID записи в базе знаний
 * @param lessonId - ID урока
 * @param questionId - ID вопроса
 * @returns Promise с массивом вариантов ответа
 */
export const getAnswerOptions = async (
  knowledgeId: string | number,
  lessonId: string | number,
  questionId: string | number
): Promise<ApiAnswerOption[]> => {
  try {
    const baseUrl = getAPIURL();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}/knowledge/${knowledgeId}/lessons/${lessonId}/test/questions/${questionId}/options`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ошибка при получении вариантов ответа:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        knowledgeId,
        lessonId,
        questionId,
      });
      throw new Error(
        `Ошибка при получении вариантов ответа: ${response.status} ${response.statusText}`
      );
    }

    const data: ApiAnswerOption[] = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка при запросе вариантов ответа:', error);
    throw error;
  }
};

/**
 * Преобразует ApiLessonItem в OrganizationalLesson
 */
const mapApiLessonItemToOrganizationalLesson = (
  apiLessonItem: ApiLessonItem,
  order: number
): OrganizationalLesson => {
  // Определяем тип урока на основе blocks
  let lessonType: 'video' | 'document' = 'document';
  let videoUrl: string | undefined;
  let videoThumbnail: string | undefined;

  // Проверяем blocks на наличие видео
  const videoBlock = apiLessonItem.blocks?.find((block) => block.type === 'video' && block.video_url);
  if (videoBlock) {
    lessonType = 'video';
    videoUrl = videoBlock.video_url;
  }

  // Используем image из урока или из первого блока
  if (apiLessonItem.image) {
    videoThumbnail = apiLessonItem.image;
  } else if (apiLessonItem.blocks && apiLessonItem.blocks.length > 0) {
    const firstBlock = apiLessonItem.blocks[0];
    if (firstBlock.media_url) {
      videoThumbnail = firstBlock.media_url;
    } else if (firstBlock.file_url) {
      videoThumbnail = firstBlock.file_url;
    }
  }

  // Преобразуем attachments из blocks
  const attachments: OrganizationalAttachment[] = [];
  if (apiLessonItem.material_file && Array.isArray(apiLessonItem.material_file) && apiLessonItem.material_file.length > 0) {
    apiLessonItem.material_file.forEach((materialUrl) => {
      const fileName = materialUrl.split('/').pop() || 'file';
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      let fileType: 'pdf' | 'zip' | 'pptx' = 'pdf';
      if (extension === 'zip' || extension === 'rar') {
        fileType = 'zip';
      } else if (extension === 'pptx' || extension === 'ppt') {
        fileType = 'pptx';
      }
      attachments.push({
        id: String(apiLessonItem.id) + '_' + fileName,
        title: fileName,
        size: '1.2 МБ', // Можно получить из API позже
        fileType,
      });
    });
  }

  // Добавляем файлы из blocks
  apiLessonItem.blocks?.forEach((block) => {
    if (block.type === 'file' && block.file_url) {
      const fileName = block.file_url.split('/').pop() || 'file';
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      let fileType: 'pdf' | 'zip' | 'pptx' = 'pdf';
      if (extension === 'zip' || extension === 'rar') {
        fileType = 'zip';
      } else if (extension === 'pptx' || extension === 'ppt') {
        fileType = 'pptx';
      }
      attachments.push({
        id: String(block.id),
        title: fileName,
        size: '1.2 МБ',
        fileType,
      });
    }
  });

  // Извлекаем title из text (первая строка)
  const textLines = apiLessonItem.text.split('\n').filter((line) => line.trim());
  const title = textLines[0] || `Урок ${order}`;
  
  // Описание - все остальные строки, кроме первой
  // Исключаем строки с метаданными типа "Сложность:", "Длительность:", "Блок:"
  const descriptionLines = textLines.slice(1).filter((line) => {
    const lowerLine = line.toLowerCase();
    return !lowerLine.startsWith('сложность:') && 
           !lowerLine.startsWith('длительность:') && 
           !lowerLine.startsWith('блок:') &&
           !lowerLine.startsWith('видео:');
  });
  const description = descriptionLines.join('\n') || '';

  // Извлекаем pages из text для документов (если нет блоков)
  const pages = lessonType === 'document' && (!apiLessonItem.blocks || apiLessonItem.blocks.length === 0) && textLines.length > 1 
    ? textLines.slice(1) 
    : undefined;

  // Преобразуем блоки
  const blocks = apiLessonItem.blocks?.map((block) => ({
    id: block.id,
    type: block.type,
    text: block.text,
    video_url: block.video_url || undefined,
    file_url: block.file_url || undefined,
    media_url: block.media_url || undefined,
    order: block.order,
  })).sort((a, b) => a.order - b.order);

  return {
    id: String(apiLessonItem.id),
    order,
    title,
    type: lessonType,
    duration: apiLessonItem.duration || '10 мин',
    difficulty: apiLessonItem.difficulty,
    updatedAt: formatDate(apiLessonItem.updated_at),
    description,
    videoThumbnail,
    videoUrl,
    attachments,
    pages,
    blocks,
    materialFile: Array.isArray(apiLessonItem.material_file) && apiLessonItem.material_file.length > 0 
      ? apiLessonItem.material_file[0] 
      : undefined,
    isFinal: apiLessonItem.test !== null, // Если есть тест, это финальный урок
    isCompleted: apiLessonItem.is_completed || false,
  };
};

/**
 * Преобразует ApiTest в QuizMeta
 */
const mapApiTestToQuizMeta = (apiTest: ApiTest): QuizMeta => {
  const questions = apiTest.questions.map((q) => {
    // Преобразуем тип вопроса: "open" -> "text"
    let questionType: 'single' | 'multiple' | 'text' = 'single';
    if (q.type === 'open') {
      questionType = 'text';
    } else if (q.type === 'multiple') {
      questionType = 'multiple';
    } else {
      questionType = 'single';
    }

    return {
      id: String(q.id),
      order: q.order,
      text: q.text,
      type: questionType,
      options: q.answer_options.map((opt) => ({
        id: String(opt.id),
        label: opt.text,
        isCorrect: opt.is_correct,
      })),
      placeholder: questionType === 'text' ? 'Введите ответ...' : undefined,
    };
  });

  return {
    duration: apiTest.time_limit || '10–15 минут',
    questionsCount: questions.length,
    passRequirement: `${Math.ceil(questions.length * 0.6)} из ${questions.length}`,
    questions,
  };
};

/**
 * Определяет статус курса на основе процента завершенных уроков
 */
const determineCourseStatus = (completedPercentage: number): OrganizationalStatus => {
  if (completedPercentage === 0) {
    return 'notStarted';
  } else if (completedPercentage === 100) {
    return 'completed';
  } else {
    return 'inProgress';
  }
};

/**
 * Преобразует ApiLesson в OrganizationalCourse
 */
export const mapApiLessonToOrganizationalCourse = (
  apiLesson: ApiLesson,
  categoryId: string = 'all'
): OrganizationalCourse => {
  const lessons = apiLesson.lessons.map((lessonItem, index) =>
    mapApiLessonItemToOrganizationalLesson(lessonItem, index + 1)
  );

  // Подсчитываем количество тестов
  const testsCount = lessons.filter((lesson) => lesson.isFinal).length;

  // Вычисляем общую длительность (суммируем duration из уроков)
  const totalMinutes = lessons.reduce((sum, lesson) => {
    const durationMatch = lesson.duration.match(/(\d+)/);
    return sum + (durationMatch ? parseInt(durationMatch[1], 10) : 10);
  }, 0);
  const duration = totalMinutes < 60 ? `${totalMinutes} мин` : `${Math.floor(totalMinutes / 60)} час ${totalMinutes % 60} мин`;

  // Формируем completionLabel
  const completedLessons = lessons.filter((lesson) => {
    // Находим соответствующий ApiLessonItem
    const apiLessonItem = apiLesson.lessons.find((item) => String(item.id) === lesson.id);
    return apiLessonItem?.is_completed || false;
  }).length;
  const completionPercent = apiLesson.completed_lessons_percentage || 0;
  const completionLabel = `${completionPercent}% завершено (${completedLessons}/${lessons.length})`;

  // Определяем badge
  const badge = apiLesson.type === 'organizational' ? 'Орг. обучение' : 'Проф. обучение';

  // Получаем quiz из первого урока с тестом
  const lessonWithTest = apiLesson.lessons.find((lesson) => lesson.test !== null);
  const quiz = lessonWithTest?.test ? mapApiTestToQuizMeta(lessonWithTest.test) : undefined;

  // Извлекаем пользовательские теги (исключая служебные)
  const customTags = apiLesson.tags
    ? apiLesson.tags.filter(
        (tag) =>
          tag !== 'organizational' &&
          tag !== 'profile' &&
          !tag.startsWith('access_')
      )
    : [];

  // Определяем categoryId из первого пользовательского тега или используем переданный
  const firstTag = customTags.length > 0 ? customTags[0] : categoryId;

  // Извлекаем логотип из первого изображения
  const logo = apiLesson.images && apiLesson.images.length > 0 ? apiLesson.images[0].image : undefined;

  return {
    id: String(apiLesson.id),
    title: apiLesson.name,
    categoryId: firstTag,
    tags: customTags,
    logo,
    lessonsCount: lessons.length,
    testsCount,
    duration,
    completionPercent,
    completionLabel,
    status: determineCourseStatus(completionPercent),
    badge,
    description: apiLesson.description || '',
    lessons,
    quiz,
  };
};

/**
 * Преобразует ApiLessonItem в Lesson для ProfileTraining
 */
const mapApiLessonItemToProfileLesson = (
  apiLessonItem: ApiLessonItem,
  order: number
): ProfileLesson => {
  // Определяем тип урока на основе blocks
  let lessonType: 'document' | 'video' | 'quiz' = 'document';
  let videoUrl: string | undefined;
  let videoThumbnail: string | undefined;

  // Проверяем blocks на наличие видео
  const videoBlock = apiLessonItem.blocks?.find((block) => block.type === 'video' && block.video_url);
  if (videoBlock) {
    lessonType = 'video';
    videoUrl = videoBlock.video_url;
  }

  // Проверяем наличие теста
  if (apiLessonItem.test) {
    lessonType = 'quiz';
  }

  // Используем image из урока или из первого блока
  if (apiLessonItem.image) {
    videoThumbnail = apiLessonItem.image;
  } else if (apiLessonItem.blocks && apiLessonItem.blocks.length > 0) {
    const firstBlock = apiLessonItem.blocks[0];
    if (firstBlock.media_url) {
      videoThumbnail = firstBlock.media_url;
    } else if (firstBlock.file_url) {
      videoThumbnail = firstBlock.file_url;
    }
  }

  // Преобразуем attachments из blocks
  const attachments: Attachment[] = [];
  if (apiLessonItem.material_file && Array.isArray(apiLessonItem.material_file) && apiLessonItem.material_file.length > 0) {
    apiLessonItem.material_file.forEach((materialUrl) => {
      const fileName = materialUrl.split('/').pop() || 'file';
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      let fileType: 'pdf' | 'zip' | 'dwg' = 'pdf';
      if (extension === 'zip' || extension === 'rar') {
        fileType = 'zip';
      } else if (extension === 'dwg' || extension === 'dxf') {
        fileType = 'dwg';
      }
      attachments.push({
        id: String(apiLessonItem.id) + '_' + fileName,
        title: fileName,
        size: '1.2 МБ',
        fileType,
      });
    });
  }

  // Добавляем файлы из blocks
  apiLessonItem.blocks?.forEach((block) => {
    if (block.type === 'file' && block.file_url) {
      const fileName = block.file_url.split('/').pop() || 'file';
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      let fileType: 'pdf' | 'zip' | 'dwg' = 'pdf';
      if (extension === 'zip' || extension === 'rar') {
        fileType = 'zip';
      } else if (extension === 'dwg' || extension === 'dxf') {
        fileType = 'dwg';
      }
      attachments.push({
        id: String(block.id),
        title: fileName,
        size: '1.2 МБ',
        fileType,
      });
    }
  });

  // Извлекаем title из text (первая строка)
  const textLines = apiLessonItem.text.split('\n').filter((line) => line.trim());
  const title = textLines[0] || `Урок ${order}`;
  const description = textLines.slice(1).join('\n') || '';

  // Извлекаем pages из text для документов
  const pages = lessonType === 'document' && textLines.length > 1 ? textLines.slice(1) : undefined;

  // Преобразуем тест в QuizMeta если есть
  const quiz = apiLessonItem.test ? mapApiTestToQuizMeta(apiLessonItem.test) : undefined;

  return {
    id: String(apiLessonItem.id),
    order,
    title,
    description,
    type: lessonType,
    duration: apiLessonItem.duration || '10 мин',
    updatedAt: formatDate(apiLessonItem.updated_at),
    attachments,
    pages,
    videoThumbnail,
    videoUrl,
    quiz,
  };
};

/**
 * Преобразует ApiLesson в Course для ProfileTraining
 */
export const mapApiLessonToCourse = (apiLesson: ApiLesson): Course => {
  const lessons = apiLesson.lessons.map((lessonItem, index) =>
    mapApiLessonItemToProfileLesson(lessonItem, index + 1)
  );

  // Подсчитываем количество тестов
  const testsCount = lessons.filter((lesson) => lesson.type === 'quiz').length;

  // Вычисляем общую длительность (суммируем duration из уроков)
  const totalMinutes = lessons.reduce((sum, lesson) => {
    const durationMatch = lesson.duration.match(/(\d+)/);
    return sum + (durationMatch ? parseInt(durationMatch[1], 10) : 10);
  }, 0);
  const duration =
    totalMinutes < 60
      ? `${totalMinutes} мин`
      : `${Math.floor(totalMinutes / 60)} час ${totalMinutes % 60} мин`;

  // Определяем badgeText и badgeStatus
  const badgeText = apiLesson.type === 'organizational' ? 'Орг. обучение' : 'Тех. обучение';
  const completionPercent = apiLesson.completed_lessons_percentage || 0;
  const badgeStatus: BadgeStatus = completionPercent === 100 ? 'completed' : 'default';

  // Определяем provider (можно извлечь из тегов или использовать дефолтное значение)
  const provider = 'AQMOL';
  const providerLogo = 'autodesk'; // Можно извлечь из тегов позже

  // Извлекаем пользовательские теги (исключая служебные)
  const customTags = apiLesson.tags
    ? apiLesson.tags.filter(
        (tag) =>
          tag !== 'organizational' &&
          tag !== 'profile' &&
          !tag.startsWith('access_')
      )
    : [];

  // Извлекаем логотип из первого изображения
  const logo = apiLesson.images && apiLesson.images.length > 0 ? apiLesson.images[0].image : undefined;

  return {
    id: String(apiLesson.id),
    title: apiLesson.name,
    description: apiLesson.description || '',
    provider,
    providerLogo,
    tags: customTags,
    logo,
    lessonsCount: lessons.length,
    testsCount,
    duration,
    completionPercent,
    badgeText,
    badgeStatus,
    lessons,
  };
};

